import React, { useState } from "react";
import {
  Table,
  Tag,
  Input,
  Select,
  Empty,
  Button,
  Dropdown,
  Drawer,
  Descriptions,
} from "antd";
import {
  Search,
  RotateCcw,
  Calendar,
  CreditCard,
  Receipt,
  FileText,
  Image as ImageIcon,
  ExternalLink,
} from "lucide-react";
import { MoreOutlined, EyeOutlined } from "@ant-design/icons";
import { useDebounce } from "use-debounce";
import dayjs from "dayjs";
import {
  useActiveExpenseCategoriesQuery,
  useExpensesQuery,
} from "../../Services";
import { PageHeader, drawerTitle, tablePagination } from "../../components";
import { PAGE_SIZE, DEBOUNCED_SEARCH_QUERY, IMAGE_URL } from "../../Constant";

export const ExpensesPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);
  const [categoryId, setCategoryId] = useState<string | null>(null);

  // Drawer states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<any | null>(null);

  // Queries
  const { data: categoriesResponse } = useActiveExpenseCategoriesQuery();
  const categories = categoriesResponse || [];

  const {
    data: expensesList,
    isLoading,
    refetch,
  } = useExpensesQuery({
    page,
    limit: PAGE_SIZE,
    search,
    category_id: categoryId,
  });

  const totalExpenses = expensesList?.total || 0;

  // Render attachments helper
  const renderAttachments = (v: string) => {
    if (!v) return <span className="text-gray-300 text-xs">-</span>;
    try {
      const files: string[] = JSON.parse(v);
      if (!Array.isArray(files) || files.length === 0)
        return <span className="text-gray-300 text-xs">-</span>;
      return (
        <div className="flex flex-wrap gap-2">
          {files.map((file, idx) => {
            const url = `${IMAGE_URL}/${file}`;
            const isPdf = file.toLowerCase().endsWith(".pdf");
            return (
              <Button
                key={idx}
                type="link"
                size="small"
                href={url}
                target="_blank"
                className="p-0 h-auto flex items-center gap-1 text-[11px] text-blue-600 font-semibold hover:text-blue-700"
              >
                {isPdf ? (
                  <FileText className="w-3.5 h-3.5" />
                ) : (
                  <ImageIcon className="w-3.5 h-3.5" />
                )}
                <span>Attachment {idx + 1}</span>
                <ExternalLink className="w-3 h-3" />
              </Button>
            );
          })}
        </div>
      );
    } catch {
      return <span className="text-gray-300 text-xs">-</span>;
    }
  };

  // Columns
  const columns = [
    {
      title: "Date",
      dataIndex: "expense_date",
      key: "expense_date",
      render: (v: string) => (
        <span className="text-xs text-gray-600 flex items-center gap-1.5 font-medium">
          <Calendar className="w-3.5 h-3.5 text-gray-400" />
          {dayjs(v).format("YYYY-MM-DD")}
        </span>
      ),
    },
    {
      title: "Expense No",
      dataIndex: "expense_no",
      key: "expense_no",
      render: (v: string) => (
        <span className="font-bold text-gray-800 text-xs">{v}</span>
      ),
    },
    {
      title: "Category",
      dataIndex: "category_name",
      key: "category_name",
      render: (v: string, record: any) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-semibold text-gray-700">{v}</span>
          {record.subcategory_name && (
            <span className="text-[10px] text-gray-400 font-medium">
              Sub: {record.subcategory_name}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Payment Info",
      dataIndex: "payment_method_name",
      key: "payment_method_name",
      render: (v: string, record: any) => (
        <div className="flex flex-col text-xs text-gray-600 font-medium">
          <span className="flex items-center gap-1">
            <CreditCard className="w-3 h-3 text-gray-400" />
            {v}
          </span>
          {record.bank_account_name && (
            <span className="text-[10px] text-gray-400">
              Acc: {record.bank_account_name}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Note / Description",
      dataIndex: "note",
      key: "note",
      width: 250,
      render: (v: string) => (
        <span
          className="text-xs text-gray-500 block max-w-xs truncate"
          title={v}
        >
          {v || <span className="text-gray-300">No description</span>}
        </span>
      ),
    },
    {
      title: "Receipts",
      dataIndex: "attachment_paths",
      key: "attachment_paths",
      render: (v: string) => {
        if (!v) return <span className="text-gray-300 text-xs">-</span>;
        try {
          const files: string[] = JSON.parse(v);
          if (!Array.isArray(files) || files.length === 0)
            return <span className="text-gray-300 text-xs">-</span>;
          return (
            <div className="flex flex-wrap gap-1">
              {files.map((file, idx) => {
                const url = `${IMAGE_URL}/${file}`;
                const isPdf = file.toLowerCase().endsWith(".pdf");
                return (
                  <Button
                    key={idx}
                    type="link"
                    size="small"
                    href={url}
                    target="_blank"
                    className="p-0 h-auto flex items-center gap-0.5 text-[10px] text-blue-600 font-semibold hover:text-blue-700"
                  >
                    {isPdf ? (
                      <FileText className="w-3 h-3" />
                    ) : (
                      <ImageIcon className="w-3 h-3" />
                    )}
                    <span>Doc {idx + 1}</span>
                    <ExternalLink className="w-2.5 h-2.5" />
                  </Button>
                );
              })}
            </div>
          );
        } catch {
          return <span className="text-gray-300 text-xs">-</span>;
        }
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      render: (v: number) => (
        <span className="font-bold text-gray-900 text-xs">
          LKR {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center" as const,
      render: (_: any, record: any) => {
        const menuItems = [
          {
            key: "view",
            label: "View Details",
            icon: <EyeOutlined />,
            onClick: () => {
              setSelectedExpense(record);
              setDrawerOpen(true);
            },
          },
        ];
        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              className="text-gray-400"
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <PageHeader
        title="Expense Ledger"
        subText={`Total logged expenses: ${totalExpenses}`}
        handleButtons={[
          { title: "", icon: RotateCcw as any, onClick: refetch },
        ]}
        searchBar={
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <Select
              allowClear
              placeholder="Category Filter"
              onChange={(v) => {
                setCategoryId(v);
                setPage(1);
              }}
              className="w-full sm:w-48 h-9 text-xs"
            >
              {categories.map((c: any) => (
                <Select.Option key={c.id} value={c.id}>
                  {c.name}
                </Select.Option>
              ))}
            </Select>

            <Input
              placeholder="Search Expense No, Note..."
              prefix={<Search className="w-3.5 h-3.5 text-gray-400 mr-1" />}
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPage(1);
              }}
              allowClear
              onClear={() => {
                setSearchInput("");
                setPage(1);
              }}
              className="w-full sm:w-64 h-9 rounded-md text-xs"
            />
          </div>
        }
      />

      <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <Table
          dataSource={expensesList?.data || []}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={tablePagination({
            current: page,
            pageSize: PAGE_SIZE,
            total: totalExpenses,
            onChange: setPage,
            itemName: "expenses",
          })}
          scroll={{ x: 1000 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No expenses found."
              />
            ),
          }}
        />
      </div>

      {/* Expense Detail Drawer */}
      <Drawer
        title={drawerTitle("Expense Transaction Details")}
        placement="right"
        width={480}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedExpense(null);
        }}
        open={drawerOpen}
      >
        {selectedExpense && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Expense Date">
              {dayjs(selectedExpense.expense_date).format("YYYY-MM-DD")}
            </Descriptions.Item>
            <Descriptions.Item label="Expense Number">
              <span className="font-bold text-blue-600">
                {selectedExpense.expense_no}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Category">
              {selectedExpense.category_name}
            </Descriptions.Item>
            {selectedExpense.subcategory_name && (
              <Descriptions.Item label="Subcategory">
                {selectedExpense.subcategory_name}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Payment Method">
              {selectedExpense.payment_method_name}
            </Descriptions.Item>
            {selectedExpense.bank_account_name && (
              <Descriptions.Item label="Bank Account">
                {selectedExpense.bank_account_name}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Amount">
              <span className="font-bold text-gray-900">
                LKR{" "}
                {parseFloat(selectedExpense.amount || 0).toLocaleString(
                  undefined,
                  {
                    minimumFractionDigits: 2,
                  },
                )}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Description/Note">
              {selectedExpense.note || (
                <span className="text-gray-300">No description</span>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Logged By">
              {selectedExpense.logged_user_name || (
                <span className="text-gray-300">-</span>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Receipts / Documents">
              {renderAttachments(selectedExpense.attachment_paths)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </div>
  );
};
